// src/pages/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { FaCloudUploadAlt, FaTrash, FaCheckCircle, FaUser, FaEnvelope, FaCar, FaFileInvoiceDollar, FaBoxOpen, FaEdit, FaTimes } from 'react-icons/fa';
import '../App.css';

// --- DATA: World Currencies & Flags ---
const WORLD_DATA = [
  { country: "USA 🇺🇸", currency: "$", label: "$ (USD)" },
  { country: "Ghana 🇬🇭", currency: "₵", label: "₵ (GHS)" },
  { country: "Nigeria 🇳🇬", currency: "₦", label: "₦ (NGN)" },
  { country: "UK 🇬🇧", currency: "£", label: "£ (GBP)" },
  { country: "Eurozone 🇪🇺", currency: "€", label: "€ (EUR)" },
  { country: "Canada 🇨🇦", currency: "C$", label: "C$ (CAD)" },
  { country: "Japan 🇯🇵", currency: "¥", label: "¥ (JPY)" },
  { country: "China 🇨🇳", currency: "¥", label: "¥ (CNY)" },
  { country: "Australia 🇦🇺", currency: "A$", label: "A$ (AUD)" },
  { country: "South Africa 🇿🇦", currency: "R", label: "R (ZAR)" },
  { country: "India 🇮🇳", currency: "₹", label: "₹ (INR)" },
  { country: "UAE 🇦🇪", currency: "AED", label: "AED (Dirham)" },
  { country: "Kenya 🇰🇪", currency: "KSh", label: "KSh (KES)" },
  { country: "Brazil 🇧🇷", currency: "R$", label: "R$ (BRL)" },
  { country: "Switzerland 🇨🇭", currency: "Fr", label: "Fr (CHF)" },
];

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('inventory'); 
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [editingId, setEditingId] = useState(null); // Track which item is being edited
  
  // Data States
  const [inventory, setInventory] = useState([]); 
  const [generalGoods, setGeneralGoods] = useState([]); 
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [testDrives, setTestDrives] = useState([]);
  const [offers, setOffers] = useState([]);
  const [preApprovals, setPreApprovals] = useState([]);
  const [goodsRequests, setGoodsRequests] = useState([]);

  // Car Form State
  const [formData, setFormData] = useState({
    make: '', model: '', year: new Date().getFullYear(),
    price: '', mileage: '', 
    bodyType: 'Sedan', // Default value
    vin: '',           // Default empty
    status: 'New', description: '',
    currency: '$', country: 'USA 🇺🇸' 
  });

  // General Goods Form State
  const [goodsFormData, setGoodsFormData] = useState({
    name: '', category: 'Electronics', price: '', 
    description: '', status: 'Available',
    currency: '$', country: 'USA 🇺🇸' 
  });

  const [images, setImages] = useState([]); 
  const [previews, setPreviews] = useState([]);

  // --- FETCH ALL DATA ---
  const fetchData = async () => {
    try {
      const carsSnap = await getDocs(collection(db, "cars"));
      setInventory(carsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const goodsSnap = await getDocs(collection(db, "general_goods"));
      setGeneralGoods(goodsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const goodsReqSnap = await getDocs(collection(db, "general_goods_requests"));
      setGoodsRequests(goodsReqSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      
      const usersSnap = await getDocs(collection(db, "users"));
      setUsers(usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const msgSnap = await getDocs(collection(db, "messages"));
      setMessages(msgSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const tdSnap = await getDocs(collection(db, "testDrives"));
      setTestDrives(tdSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const offSnap = await getDocs(collection(db, "offers"));
      setOffers(offSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

      const preSnap = await getDocs(collection(db, "preApprovals"));
      setPreApprovals(preSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // --- ACTIONS ---
  const handleDelete = async (collectionName, id) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteDoc(doc(db, collectionName, id));
        fetchData();
        alert("Deleted successfully.");
      } catch (error) {
        console.error("Error deleting:", error);
      }
    }
  };

  const handleToggleSold = async (collectionName, id, currentStatus) => {
      const isSold = currentStatus === 'Sold' || currentStatus === 'Sold Out';
      const newStatus = isSold ? (collectionName === 'cars' ? 'New' : 'Available') : (collectionName === 'cars' ? 'Sold' : 'Sold Out');
      
      try {
          await updateDoc(doc(db, collectionName, id), { status: newStatus });
          fetchData();
      } catch (error) {
          console.error("Error updating status:", error);
      }
  };

  const handleApprove = async (id) => {
      try {
          await updateDoc(doc(db, "preApprovals", id), { status: 'Approved' });
          fetchData();
          alert("User Pre-Approved!");
      } catch (error) {
          console.error(error);
      }
  };

  // --- EDIT FUNCTIONS ---
  const handleEditCar = (car) => {
      setEditingId(car.id);
      setFormData({
          make: car.make, 
          model: car.model, 
          year: car.year, 
          price: car.price, 
          mileage: car.mileage,
          bodyType: car.bodyType || 'Sedan',
          vin: car.vin || '',
          status: car.status,
          description: car.description || '',
          currency: car.currency || '$', 
          country: car.country || 'USA 🇺🇸'
      });
      setImages([]); // Clear images as we don't edit them
      setPreviews([]);
      window.scrollTo(0, 0); // Scroll to form
  };

  const handleEditGoods = (item) => {
      setEditingId(item.id);
      setGoodsFormData({
          name: item.name, 
          category: item.category, 
          price: item.price, 
          description: item.description, 
          status: item.status, 
          currency: item.currency || '$', 
          country: item.country || 'USA 🇺🇸' 
      });
      setImages([]); // Clear images as we don't edit them
      setPreviews([]);
      window.scrollTo(0, 0); // Scroll to form
  };

  const cancelEdit = () => {
      setEditingId(null);
      setFormData({ 
        make: '', model: '', year: new Date().getFullYear(), price: '', mileage: '', 
        bodyType: 'Sedan', vin: '', status: 'New', description: '',
        currency: '$', country: 'USA 🇺🇸'
      });
      setGoodsFormData({ 
        name: '', category: 'Electronics', price: '', 
        description: '', status: 'Available',
        currency: '$', country: 'USA 🇺🇸' 
      });
      setImages([]);
      setPreviews([]);
  };

  // --- FORM HANDLERS ---
  
  // Logic: When currency changes, auto-select country
  const updateCurrencyAndCountry = (e, currentData, setFunction) => {
      const { name, value } = e.target;
      let updates = { [name]: value };

      if (name === 'currency') {
          const match = WORLD_DATA.find(item => item.currency === value);
          if (match) {
              updates.country = match.country;
          }
      }

      setFunction({ ...currentData, ...updates });
  };

  const handleChange = (e) => updateCurrencyAndCountry(e, formData, setFormData);
  const handleGoodsChange = (e) => updateCurrencyAndCountry(e, goodsFormData, setGoodsFormData);
  
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Logic for limits based on active tab
    const isGeneralGoods = activeTab === 'general_goods';
    const limit = isGeneralGoods ? 1 : 8;

    if (images.length + files.length > limit) { 
        alert(`Maximum ${limit} image${limit > 1 ? 's' : ''} allowed for this category.`); 
        return; 
    }

    setImages([...images, ...files]);
    setPreviews([...previews, ...files.map(file => URL.createObjectURL(file))]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
    setPreviews(previews.filter((_, i) => i !== index));
  };

  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "cremlorallc"); 
    formData.append("cloud_name", "dmq9vbysj"); 
    const res = await axios.post("https://api.cloudinary.com/v1_1/dmq9vbysj/image/upload", formData);
    return res.data.secure_url;
  };

  // Submit Car
  const handleSubmitCar = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
          // UPDATE Logic (No Images)
          await updateDoc(doc(db, "cars", editingId), {
              ...formData,
              price: Number(formData.price),
              mileage: Number(formData.mileage),
              year: Number(formData.year)
          });
          alert("Vehicle updated successfully!");
          setEditingId(null);
      } else {
          // CREATE Logic (With Images)
          const imageUrls = [];
          for (const file of images) { imageUrls.push(await uploadToCloudinary(file)); }
          
          await addDoc(collection(db, "cars"), {
            ...formData,
            price: Number(formData.price),
            mileage: Number(formData.mileage),
            year: Number(formData.year),
            images: imageUrls,
            image: imageUrls[0] || '',
            createdAt: new Date()
          });
          setSuccess(true);
      }

      setLoading(false); 
      setFormData({ 
        make: '', model: '', year: new Date().getFullYear(), price: '', mileage: '', 
        bodyType: 'Sedan', vin: '', status: 'New', description: '',
        currency: '$', country: 'USA 🇺🇸'
      });
      setImages([]); setPreviews([]);
      fetchData();
      window.scrollTo(0,0);
    } catch (error) {
      console.error(error); setLoading(false);
    }
  };

  // Submit General Good
  const handleSubmitGoods = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
          // UPDATE Logic (No Images)
          await updateDoc(doc(db, "general_goods", editingId), {
              ...goodsFormData,
              price: Number(goodsFormData.price)
          });
          alert("Item updated successfully!");
          setEditingId(null);
      } else {
          // CREATE Logic (With Images)
          const imageUrls = [];
          for (const file of images) { imageUrls.push(await uploadToCloudinary(file)); }
          
          await addDoc(collection(db, "general_goods"), {
            ...goodsFormData,
            price: Number(goodsFormData.price),
            images: imageUrls,
            image: imageUrls[0] || '',
            createdAt: new Date()
          });
          setSuccess(true);
      }

      setLoading(false); 
      setGoodsFormData({ 
        name: '', category: 'Electronics', price: '', description: '', status: 'Available', currency: '$', country: 'USA 🇺🇸'
      });
      setImages([]); setPreviews([]);
      fetchData();
      window.scrollTo(0,0);
    } catch (error) {
      console.error(error); setLoading(false);
    }
  };

  return (
    <div className="container" style={{ padding: '40px 20px' }}>
        <h1 style={{ marginBottom: '20px', borderBottom: '2px solid var(--accent-blue)' }}>Admin Dashboard</h1>
        
        {/* Tabs */}
        <div style={{display: 'flex', gap: '10px', marginBottom: '30px', flexWrap: 'wrap'}}>
            <button onClick={()=>setActiveTab('inventory')} className={`btn ${activeTab==='inventory'?'btn-primary':'btn-outline'}`}><FaCar/> Dealership (Cars)</button>
            <button onClick={()=>setActiveTab('general_goods')} className={`btn ${activeTab==='general_goods'?'btn-primary':'btn-outline'}`}><FaBoxOpen/> General Goods</button>
            <button onClick={()=>setActiveTab('users')} className={`btn ${activeTab==='users'?'btn-primary':'btn-outline'}`}><FaUser/> Users</button>
            <button onClick={()=>setActiveTab('messages')} className={`btn ${activeTab==='messages'?'btn-primary':'btn-outline'}`}><FaEnvelope/> Messages</button>
            <button onClick={()=>setActiveTab('forms')} className={`btn ${activeTab==='forms'?'btn-primary':'btn-outline'}`}><FaFileInvoiceDollar/> Forms & Requests</button>
        </div>

        {/* --- DEALERSHIP (CARS) TAB --- */}
        {activeTab === 'inventory' && (
            <div>
                <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #ddd' }}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <h3>{editingId ? 'Edit Vehicle' : 'Add New Vehicle'}</h3>
                        {editingId && <button onClick={cancelEdit} className="btn" style={{background:'#ccc', fontSize:'0.8rem', padding:'5px 10px'}}><FaTimes/> Cancel Edit</button>}
                    </div>
                    
                    {success && !editingId && <div style={{color:'green', margin:'10px 0'}}><FaCheckCircle/> Posted!</div>}
                    
                    <form onSubmit={handleSubmitCar}>
                        <div className="grid-2">
                            <input type="text" name="make" value={formData.make} onChange={handleChange} placeholder="Make" required />
                            <input type="text" name="model" value={formData.model} onChange={handleChange} placeholder="Model" required />
                        </div>
                        <div className="grid-3">
                            <input type="number" name="year" value={formData.year} onChange={handleChange} placeholder="Year" required />
                            <input type="number" name="price" value={formData.price} onChange={handleChange} placeholder="Price" required />
                            <input type="number" name="mileage" value={formData.mileage} onChange={handleChange} placeholder="Mileage" required />
                        </div>
                        
                        <div className="grid-2">
                            <select name="bodyType" value={formData.bodyType} onChange={handleChange} style={{padding:'10px'}}>
                                <option value="Sedan">Sedan</option>
                                <option value="SUV">SUV</option>
                                <option value="Truck">Truck</option>
                                <option value="Coupe">Coupe</option>
                                <option value="Van">Van</option>
                                <option value="Convertible">Convertible</option>
                                <option value="Hatchback">Hatchback</option>
                                <option value="Wagon">Wagon</option>
                            </select>
                            <input type="text" name="vin" value={formData.vin} onChange={handleChange} placeholder="VIN (Vehicle Identification Number)" required />
                        </div>

                        <div className="grid-3">
                            <select name="status" value={formData.status} onChange={handleChange} style={{padding:'10px'}}>
                                <option value="New">New</option>
                                <option value="Used">Used</option>
                            </select>
                            <select name="currency" value={formData.currency} onChange={handleChange} style={{padding:'10px'}}>
                                {WORLD_DATA.map(c => <option key={c.label} value={c.currency}>{c.label}</option>)}
                            </select>
                            <select name="country" value={formData.country} onChange={handleChange} style={{padding:'10px'}}>
                                {WORLD_DATA.map(c => <option key={c.country} value={c.country}>{c.country}</option>)}
                            </select>
                        </div>

                        {/* Hide Image Upload when editing */}
                        {!editingId && (
                            <div style={{marginBottom:'15px'}}>
                                 <input type="file" id="file-upload-car" multiple accept="image/*" onChange={handleImageChange} style={{display: 'none'}} />
                                 <label htmlFor="file-upload-car" className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                    <FaCloudUploadAlt /> Choose Images
                                 </label>
                                 <span style={{marginLeft: '10px', fontSize: '0.9rem', color: '#666'}}>{images.length} / 8 selected</span>
                                 <div style={{display:'flex', gap:'5px', marginTop:'10px'}}>
                                    {previews.map((src, i) => (
                                        <div key={i} style={{position:'relative'}}>
                                            <img src={src} width="50" height="50" alt="" style={{objectFit: 'cover', borderRadius: '4px'}} />
                                            <span onClick={()=>removeImage(i)} style={{position:'absolute', top:-5, right:-5, background:'red', color:'white', cursor:'pointer', borderRadius:'50%', width:'18px', height:'18px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px'}}>x</span>
                                        </div>
                                    ))}
                                 </div>
                            </div>
                        )}
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Processing...' : (editingId ? 'Update Vehicle' : 'Post Vehicle')}
                        </button>
                    </form>
                </div>

                <h3>Current Dealership Inventory</h3>
                <div style={{display: 'grid', gap: '15px'}}>
                    {inventory.map(car => (
                        <div key={car.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '15px', border: '1px solid #ddd', borderRadius: '8px' }}>
                             <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
                                <img src={car.image} style={{width:'60px', height:'60px', objectFit:'cover', opacity: car.status === 'Sold' ? 0.5 : 1}} alt=""/>
                                <div>
                                    <h4>{car.year} {car.make} {car.model}</h4>
                                    <div style={{fontSize: '0.85rem', color: '#555', marginBottom: '4px'}}>VIN: {car.vin || 'N/A'}</div>
                                    <span style={{color: car.status === 'Sold' ? 'red' : 'green', fontWeight:'bold'}}>{car.status}</span>
                                    <div style={{fontSize: '0.9rem', color: '#666'}}>
                                       Available in: {car.country} | {car.currency}{car.price}
                                    </div>
                                </div>
                             </div>
                             <div>
                                <button onClick={() => handleEditCar(car)} style={{marginRight:'10px', background: '#007bff', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}>
                                    <FaEdit/> Edit
                                </button>
                                <button onClick={() => handleToggleSold('cars', car.id, car.status)} style={{marginRight:'10px', background: car.status === 'Sold' ? 'green' : 'orange', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}>
                                    {car.status === 'Sold' ? 'Mark Available' : 'Mark Sold'}
                                </button>
                                <button onClick={() => handleDelete('cars', car.id)} style={{background:'red', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}><FaTrash/></button>
                             </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* --- GENERAL GOODS TAB --- */}
        {activeTab === 'general_goods' && (
            <div>
                <div style={{ background: 'white', padding: '20px', borderRadius: '8px', marginBottom: '30px', border: '1px solid #ddd' }}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <h3>{editingId ? 'Edit Item' : 'Add General Goods'}</h3>
                        {editingId && <button onClick={cancelEdit} className="btn" style={{background:'#ccc', fontSize:'0.8rem', padding:'5px 10px'}}><FaTimes/> Cancel Edit</button>}
                    </div>

                    {success && !editingId && <div style={{color:'green', margin:'10px 0'}}><FaCheckCircle/> Posted!</div>}
                    
                    <form onSubmit={handleSubmitGoods}>
                        <div className="grid-2">
                            <input type="text" name="name" value={goodsFormData.name} onChange={handleGoodsChange} placeholder="Item Name (e.g., Piano, Fridge)" required />
                            <select name="category" value={goodsFormData.category} onChange={handleGoodsChange}>
                                <option value="Electronics">Electronics</option>
                                <option value="Home and Kitchen Appliances">Home and Kitchen Appliances</option>
                                <option value="Musical Instruments">Musical Instruments</option>
                                <option value="Furniture">Furniture</option>
                                <option value="Vehicle Parts">Vehicle Parts</option>
                                <option value="Merchandise">Merchandise</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        <div className="grid-3">
                            <input type="number" name="price" value={goodsFormData.price} onChange={handleGoodsChange} placeholder="Price" required />
                            <select name="currency" value={goodsFormData.currency} onChange={handleGoodsChange}>
                                {WORLD_DATA.map(c => <option key={c.label} value={c.currency}>{c.label}</option>)}
                            </select>
                            <select name="country" value={goodsFormData.country} onChange={handleGoodsChange}>
                                {WORLD_DATA.map(c => <option key={c.country} value={c.country}>{c.country}</option>)}
                            </select>
                        </div>
                        <textarea name="description" value={goodsFormData.description} onChange={handleGoodsChange} placeholder="Item Description" rows="3" style={{width:'100%', marginBottom:'15px'}}></textarea>

                        {/* Hide Image Upload when editing */}
                        {!editingId && (
                            <div style={{marginBottom:'15px'}}>
                                 <input type="file" id="file-upload-goods" multiple accept="image/*" onChange={handleImageChange} style={{display: 'none'}} />
                                 <label htmlFor="file-upload-goods" className="btn btn-outline" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                    <FaCloudUploadAlt /> Choose Images
                                 </label>
                                 <span style={{marginLeft: '10px', fontSize: '0.9rem', color: '#666'}}>{images.length} / 1 selected</span>
                                 
                                 <div style={{display:'flex', gap:'5px', marginTop:'10px'}}>
                                    {previews.map((src, i) => (
                                        <div key={i} style={{position:'relative'}}>
                                            <img src={src} width="50" height="50" alt="" style={{objectFit: 'cover', borderRadius: '4px'}} />
                                            <span onClick={()=>removeImage(i)} style={{position:'absolute', top:-5, right:-5, background:'red', color:'white', cursor:'pointer', borderRadius:'50%', width:'18px', height:'18px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'12px'}}>x</span>
                                        </div>
                                    ))}
                                 </div>
                            </div>
                        )}
                        <button type="submit" className="btn btn-primary" disabled={loading}>
                            {loading ? 'Processing...' : (editingId ? 'Update Item' : 'Post Item')}
                        </button>
                    </form>
                </div>

                <h3>Current General Goods</h3>
                <div style={{display: 'grid', gap: '15px'}}>
                    {generalGoods.map(item => (
                        <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', padding: '15px', border: '1px solid #ddd', borderRadius: '8px' }}>
                             <div style={{display:'flex', gap:'15px', alignItems:'center'}}>
                                <img src={item.image} style={{width:'60px', height:'60px', objectFit:'cover', opacity: item.status === 'Sold Out' ? 0.5 : 1}} alt=""/>
                                <div>
                                    <h4>{item.name}</h4>
                                    <div style={{fontSize: '0.9rem', color: '#666'}}>
                                       Available in: {item.country} | {item.category}
                                    </div>
                                    <div style={{fontWeight: 'bold'}}>{item.currency}{item.price}</div>
                                </div>
                             </div>
                             <div>
                                <button onClick={() => handleEditGoods(item)} style={{marginRight:'10px', background: '#007bff', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}>
                                    <FaEdit/> Edit
                                </button>
                                <button onClick={() => handleToggleSold('general_goods', item.id, item.status)} style={{marginRight:'10px', background: item.status === 'Sold Out' ? 'green' : 'orange', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}>
                                    {item.status === 'Sold Out' ? 'Mark Available' : 'Mark Sold'}
                                </button>
                                <button onClick={() => handleDelete('general_goods', item.id)} style={{background:'red', color:'white', border:'none', padding:'5px 10px', borderRadius:'4px', cursor:'pointer'}}><FaTrash/></button>
                             </div>
                        </div>
                    ))}
                </div>
            </div>
        )}

        {/* --- USERS, MESSAGES, FORMS TABS --- */}
        {activeTab === 'users' && (
            <div>
                <h3>Registered Users</h3>
                <table style={{width:'100%', borderCollapse:'collapse', background:'white'}}>
                    <thead>
                        <tr style={{background:'#f4f4f4', textAlign:'left'}}><th style={{padding:'10px'}}>Email</th><th style={{padding:'10px'}}>Role</th></tr>
                    </thead>
                    <tbody>
                        {users.map(u => (
                            <tr key={u.id} style={{borderBottom:'1px solid #ddd'}}>
                                <td style={{padding:'10px'}}>{u.email}</td>
                                <td style={{padding:'10px'}}>{u.role || 'User'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}
        {activeTab === 'messages' && (
             <div>
                 <h3>Contact Form Messages</h3>
                 {messages.length === 0 && <p>No messages.</p>}
                 {messages.map(m => (
                     <div key={m.id} style={{background:'white', padding:'15px', marginBottom:'10px', border:'1px solid #ddd'}}>
                         <p><strong>From:</strong> {m.fullName} ({m.email})</p>
                         <p><strong>Phone:</strong> {m.fullPhone}</p>
                         <p><strong>Subject:</strong> {m.subject}</p>
                         <p style={{background:'#f9f9f9', padding:'10px'}}>{m.message}</p>
                     </div>
                 ))}
            </div>
        )}
        {activeTab === 'forms' && (
            <div>
                <h3>General Goods Orders</h3>
                {goodsRequests.length === 0 && <p style={{color:'#666', fontStyle:'italic'}}>No new orders.</p>}
                {goodsRequests.map(req => (
                    <div key={req.id} style={{padding:'15px', background:'white', marginBottom:'15px', border:'1px solid #ddd', borderLeft: '4px solid var(--accent-blue)'}}>
                        <div style={{display:'flex', justifyContent:'space-between'}}>
                             <h4 style={{margin:0, color: '#051337'}}>Order for: {req.itemName}</h4>
                             <span style={{fontWeight:'bold'}}>{req.itemCurrency}{req.itemPrice}</span>
                        </div>
                        <hr style={{margin:'10px 0', border:'0', borderTop:'1px solid #eee'}} />
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', fontSize:'0.95rem'}}>
                            <div><strong>Name:</strong> {req.fullName}</div>
                            <div><strong>Phone:</strong> {req.phone}</div>
                            <div><strong>Location:</strong> {req.location}</div>
                            <div><strong>Address:</strong> {req.address}</div>
                        </div>
                    </div>
                ))}
                
                 <h3 style={{marginTop:'40px', borderTop:'2px solid #ddd', paddingTop:'20px'}}>Test Drive Requests</h3>
                {testDrives.map(td => (
                    <div key={td.id} style={{padding:'10px', background:'white', marginBottom:'5px', border:'1px solid #ddd'}}>
                        <strong>{td.carName}</strong> - {td.date} @ {td.time} <br/>
                        User: {td.userEmail} | Phone: {td.phone}
                    </div>
                ))}

                <h3 style={{marginTop:'30px'}}>Offers</h3>
                {offers.map(o => (
                    <div key={o.id} style={{padding:'10px', background:'white', marginBottom:'5px', border:'1px solid #ddd'}}>
                        <strong>{o.carName}</strong> - Offer: ${o.offerAmount} <br/>
                        User: {o.userEmail} <br/>
                        Comments: {o.comments}
                    </div>
                ))}

                <h3 style={{marginTop:'30px'}}>Pre-Approval Requests</h3>
                {preApprovals.map(pa => (
                    <div key={pa.id} style={{padding:'10px', background:'white', marginBottom:'5px', border:'1px solid #ddd', display:'flex', justifyContent:'space-between'}}>
                        <div>
                            <strong>{pa.fullName}</strong> (Income: ${pa.annualIncome}) <br/>
                            Status: <span style={{color: pa.status==='Approved'?'green':'orange'}}>{pa.status}</span> <br/>
                            User: {pa.userEmail}
                        </div>
                        {pa.status !== 'Approved' && (
                            <button onClick={()=>handleApprove(pa.id)} style={{background:'green', color:'white', border:'none', padding:'5px 15px', cursor:'pointer', borderRadius:'4px'}}>Approve</button>
                        )}
                    </div>
                ))}
            </div>
        )}
    </div>
  );
};

export default AdminDashboard;